import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

// ── Validation helpers ────────────────────────────────────────────────────────

function validateAimsRow(row, testList) {
    const errors = {};
    const test = String(row['Test'] ?? '').trim();

    if (!test) {
        errors['Test'] = 'Test name is required';
    } else if (!testList.some(t => t.TestName === test)) {
        errors['Test'] = 'Test not found in master test list';
    }

    const charge = row['AIMS Charge'];
    if (charge === '' || charge === undefined || charge === null) {
        errors['AIMS Charge'] = 'Required';
    } else if (isNaN(Number(charge)) || Number(charge) < 0) {
        errors['AIMS Charge'] = 'Must be a valid non-negative number';
    }

    return errors;
}

function validatePriceListRow(row, testList, userTypes) {
    const errors = {};
    const testName = String(row['Test Name'] ?? '').trim();
    const userType = String(row['User Type'] ?? '').trim();

    if (!testName) {
        errors['Test Name'] = 'Test name is required';
    } else if (!testList.some(t => t.TestName === testName)) {
        errors['Test Name'] = 'Test not found in master test list';
    }

    if (!userType) {
        errors['User Type'] = 'User type is required';
    } else if (!userTypes.includes(userType)) {
        errors['User Type'] = 'Invalid user type';
    }

    ['Serum', 'CSF', 'Serum + CSF'].forEach(f => {
        const val = row[f];
        if (val !== '' && val !== undefined && val !== null) {
            if (isNaN(Number(val)) || Number(val) < 0) {
                errors[f] = 'Must be a valid non-negative number';
            }
        }
    });

    return errors;
}

function validateTestRow(row) {
    const errors = {};
    const name = String(row['Test Name'] ?? '').trim();
    if (!name) errors['Test Name'] = 'Test name is required';

    const tat = row['TAT (Days)'];
    if (tat !== '' && tat !== undefined && tat !== null) {
        if (isNaN(Number(tat)) || Number(tat) < 0) {
            errors['TAT (Days)'] = 'Must be a valid non-negative number';
        }
    }

    return errors;
}

// Resolve a "Hospital" (+ optional "City") cell to a specific hospital record
function resolveHospital(row, hospitals, cities) {
    const hospitalName = String(row['Hospital'] ?? '').trim();
    const cityName = String(row['City'] ?? '').trim();
    if (!hospitalName) return { error: 'Hospital is required' };

    const cityById = new Map(cities.map(c => [String(c.id), c]));
    let matches = hospitals.filter(
        h => String(h.hospitalName ?? '').trim().toLowerCase() === hospitalName.toLowerCase()
    );
    if (cityName) {
        matches = matches.filter(h => {
            const c = cityById.get(String(h.city_id));
            return c && String(c.name ?? '').trim().toLowerCase() === cityName.toLowerCase();
        });
    }

    if (matches.length === 0) {
        return {
            error: cityName
                ? `No hospital "${hospitalName}" found in city "${cityName}"`
                : `Hospital "${hospitalName}" not found`,
        };
    }
    if (matches.length > 1) {
        return { error: `Multiple hospitals named "${hospitalName}" found — add a City to disambiguate` };
    }
    return { hospital: matches[0] };
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function validateDoctorRow(row, hospitals, cities) {
    const errors = {};
    const name = String(row['Doctor Name'] ?? '').trim();
    if (!name) errors['Doctor Name'] = 'Doctor name is required';

    const { hospital, error: hospitalError } = resolveHospital(row, hospitals, cities);
    if (hospitalError) errors['Hospital'] = hospitalError;

    const mobile = String(row['Mobile Number'] ?? '').trim();
    if (mobile && !/^\d{10}$/.test(mobile)) {
        errors['Mobile Number'] = 'Must be exactly 10 digits';
    }

    const email = String(row['Email'] ?? '').trim();
    if (email && !EMAIL_REGEX.test(email)) {
        errors['Email'] = 'Invalid email format';
    }

    return { errors, hospital };
}

// Resolve a "City" cell to a specific city record (city names are unique)
function resolveCity(row, cities) {
    const cityName = String(row['City'] ?? '').trim();
    if (!cityName) return { error: 'City is required' };

    const match = (cities || []).find(
        c => String(c.name ?? '').trim().toLowerCase() === cityName.toLowerCase()
    );
    if (!match) return { error: `City "${cityName}" not found` };
    return { city: match };
}

function validateHospitalRow(row, cities) {
    const errors = {};
    const name = String(row['Hospital Name'] ?? '').trim();
    if (!name) errors['Hospital Name'] = 'Hospital name is required';

    const { city, error: cityError } = resolveCity(row, cities);
    if (cityError) errors['City'] = cityError;

    const pincode = String(row['Pincode'] ?? '').trim();
    if (pincode && !/^\d{6}$/.test(pincode)) {
        errors['Pincode'] = 'Must be exactly 6 digits';
    }

    return { errors, city };
}

// Validates all rows and also checks for within-batch and existing-DB duplicates
function validateAllRows(rows, ctx) {
    const { testList, userTypes, isAims, isDoctors, isHospitals, isTests, hospitals, cities, existingDoctors, existingHospitals, existingTests } = ctx;

    if (isTests) {
        const withErrors = rows.map(row => ({ ...row, _errors: validateTestRow(row) }));

        return withErrors.map((row, idx) => {
            const errors = { ...row._errors };
            const name = String(row['Test Name'] ?? '').trim().toLowerCase();
            let duplicateOf = null;

            if (name && !errors['Test Name']) {
                const dupIdx = withErrors.findIndex(
                    (r, i) => i !== idx && String(r['Test Name'] ?? '').trim().toLowerCase() === name
                );
                if (dupIdx !== -1) {
                    errors['Test Name'] = `Duplicate of row ${dupIdx + 1} in this file`;
                } else {
                    duplicateOf = (existingTests || []).find(
                        t => String(t.TestName ?? '').trim().toLowerCase() === name
                    ) || null;
                }
            }

            return { ...row, _errors: errors, _duplicateOf: duplicateOf };
        });
    }

    if (isDoctors) {
        const withInfo = rows.map(row => {
            const { errors, hospital } = validateDoctorRow(row, hospitals || [], cities || []);
            return { ...row, _errors: errors, _hospital: hospital };
        });

        return withInfo.map((row, idx) => {
            const errors = { ...row._errors };
            const name = String(row['Doctor Name'] ?? '').trim().toLowerCase();
            const hospitalId = row._hospital?.id;

            if (name && hospitalId && !errors['Doctor Name'] && !errors['Hospital']) {
                // The resolved hospital includes its city; keep the first valid occurrence.
                const dupIdx = withInfo.findIndex(
                    (r, i) =>
                        i < idx &&
                        Object.keys(r._errors).length === 0 &&
                        String(r['Doctor Name'] ?? '').trim().toLowerCase() === name &&
                        r._hospital?.id === hospitalId
                );
                if (dupIdx !== -1) {
                    errors['Doctor Name'] = `Duplicate of row ${dupIdx + 1} in this file`;
                } else {
                    const existsInDb = (existingDoctors || []).some(
                        d =>
                            String(d.doctorName ?? '').trim().toLowerCase() === name &&
                            String(d.hospital_id) === String(hospitalId)
                    );
                    if (existsInDb) {
                        errors['Doctor Name'] = 'A doctor with this name already exists at this hospital';
                    }
                }
            }

            return { ...row, _errors: errors };
        });
    }

    if (isHospitals) {
        const withInfo = rows.map(row => {
            const { errors, city } = validateHospitalRow(row, cities || []);
            return { ...row, _errors: errors, _city: city };
        });

        return withInfo.map((row, idx) => {
            const errors = { ...row._errors };
            const name = String(row['Hospital Name'] ?? '').trim().toLowerCase();
            const cityId = row._city?.id;

            if (name && cityId && !errors['Hospital Name'] && !errors['City']) {
                // Keep the first valid occurrence available for import.
                const dupIdx = withInfo.findIndex(
                    (r, i) =>
                        i < idx &&
                        Object.keys(r._errors).length === 0 &&
                        String(r['Hospital Name'] ?? '').trim().toLowerCase() === name &&
                        r._city?.id === cityId
                );
                if (dupIdx !== -1) {
                    errors['Hospital Name'] = `Duplicate of row ${dupIdx + 1} in this file`;
                } else {
                    const existsInDb = (existingHospitals || []).some(
                        h =>
                            String(h.hospitalName ?? '').trim().toLowerCase() === name &&
                            String(h.city_id) === String(cityId)
                    );
                    if (existsInDb) {
                        errors['Hospital Name'] = 'A hospital with this name already exists in this city';
                    }
                }
            }

            return { ...row, _errors: errors };
        });
    }

    const withErrors = rows.map(row => ({
        ...row,
        _errors: isAims
            ? validateAimsRow(row, testList)
            : validatePriceListRow(row, testList, userTypes),
    }));

    return withErrors.map((row, idx) => {
        const errors = { ...row._errors };

        if (isAims) {
            const test = String(row['Test'] ?? '').trim();
            if (test && !errors['Test']) {
                const dupIdx = withErrors.findIndex(
                    (r, i) => i !== idx && String(r['Test'] ?? '').trim() === test
                );
                if (dupIdx !== -1) errors['Test'] = `Duplicate of row ${dupIdx + 1}`;
            }
        } else {
            const testName = String(row['Test Name'] ?? '').trim();
            const userType = String(row['User Type'] ?? '').trim();
            if (testName && userType && !errors['Test Name']) {
                const dupIdx = withErrors.findIndex(
                    (r, i) =>
                        i !== idx &&
                        String(r['Test Name'] ?? '').trim() === testName &&
                        String(r['User Type'] ?? '').trim() === userType
                );
                if (dupIdx !== -1) errors['Test Name'] = `Duplicate of row ${dupIdx + 1}`;
            }
        }

        return { ...row, _errors: errors };
    });
}

// ── Main component ────────────────────────────────────────────────────────────

const BulkUploadModal = ({ type, testList, userTypes, hospitals, cities, existingDoctors, existingHospitals, existingTests, onImport, onClose }) => {
    const isAims = type === 'aims';
    const isDoctors = type === 'doctors';
    const isHospitals = type === 'hospitals';
    const isTests = type === 'tests';
    const [step, setStep] = useState('upload');
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [duplicateActions, setDuplicateActions] = useState({}); // row._id -> 'replace' | 'skip'
    const [bulkTat, setBulkTat] = useState('');
    const [importing, setImporting] = useState(false);
    const [highlightRow, setHighlightRow] = useState(null);
    const fileInputRef = useRef(null);

    const isRowValid = row => Object.keys(row._errors).length === 0;
    const isDuplicateRow = row => isTests && !!row._duplicateOf;

    const validationCtx = { testList, userTypes, isAims, isDoctors, isHospitals, isTests, hospitals, cities, existingDoctors, existingHospitals, existingTests };

    const headers = isDoctors
        ? ['Doctor Name', 'Hospital', 'City', 'Mobile Number', 'Email']
        : isHospitals
        ? ['Hospital Name', 'City', 'Address', 'Pincode']
        : isAims
        ? ['Test', 'AIMS Charge']
        : isTests
        ? ['Test Name', 'TAT (Days)']
        : ['Test Name', 'User Type', 'Serum', 'CSF', 'Serum + CSF'];

    const modalTitle = isDoctors ? 'Doctors' : isHospitals ? 'Hospitals' : isAims ? 'AIMS Charges' : isTests ? 'Tests' : 'Price List';

    // Mark a duplicate row's resolution and keep the selection set in sync
    const setDuplicateAction = (id, action) => {
        setDuplicateActions(prev => ({ ...prev, [id]: action }));
        setSelected(prev => {
            const next = new Set(prev);
            if (action === 'replace') next.add(id);
            else next.delete(id);
            return next;
        });
    };

    // Download template with sample row
    const downloadTemplate = () => {
        const sample = isDoctors
            ? [{ 'Doctor Name': 'Dr. John Smith', Hospital: 'Apollo Hospital', City: 'Chennai', 'Mobile Number': '9876543210', Email: 'john.smith@example.com' }]
            : isHospitals
            ? [{ 'Hospital Name': 'Apollo Hospital', City: 'Chennai', Address: '21 Greams Lane', Pincode: '600006' }]
            : isAims
            ? [{ Test: 'Anti-NMDAR Antibody', 'AIMS Charge': 1200 }]
            : isTests
            ? [{ 'Test Name': 'Anti-NMDAR Antibody', 'TAT (Days)': 7 }]
            : [{ 'Test Name': 'Anti-NMDAR Antibody', 'User Type': 'Thru Sales Guys', Serum: 1500, CSF: 1800, 'Serum + CSF': 2500 }];

        const ws = XLSX.utils.json_to_sheet(sample);
        ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 4, 22) }));
        headers.forEach((_, i) => {
            const cell = XLSX.utils.encode_cell({ r: 0, c: i });
            if (ws[cell]) {
                ws[cell].s = { fill: { fgColor: { rgb: 'FFFF00' } }, font: { bold: true } };
            }
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, isDoctors ? 'Doctors_Template.xlsx' : isHospitals ? 'Hospitals_Template.xlsx' : isAims ? 'AIMS_Charges_Template.xlsx' : isTests ? 'Tests_Template.xlsx' : 'PriceList_Template.xlsx');
    };

    const exportInvalidRows = () => {
        const invalidRows = rows.flatMap((row, index) => {
            if (isRowValid(row)) return [];
            const exportedRow = {};
            headers.forEach(header => { exportedRow[header] = row[header] ?? ''; });
            exportedRow['Preview Row'] = index + 1;
            exportedRow['Validation Errors'] = Object.entries(row._errors)
                .map(([field, message]) => `${field}: ${message}`)
                .join('; ');
            return [exportedRow];
        });
        if (!invalidRows.length) return;

        try {
            const ws = XLSX.utils.json_to_sheet(invalidRows, {
                header: [...headers, 'Preview Row', 'Validation Errors'],
            });
            ws['!cols'] = [...headers.map(header => ({ wch: Math.max(header.length + 4, 22) })), { wch: 14 }, { wch: 80 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Invalid Rows');
            XLSX.writeFile(wb, `${modalTitle.replace(/\s+/g, '_')}_Invalid_Rows.xlsx`);
        } catch {
            toast.error('Failed to export invalid rows. Please try again.');
        }
    };

    // Parse uploaded Excel file
    const handleFile = e => {
        const file = e.target.files[0];
        if (!file) return;
        if (!/\.(xlsx|xls)$/i.test(file.name)) {
            toast.error('Please upload an Excel file (.xlsx or .xls)');
            e.target.value = '';
            return;
        }
        const reader = new FileReader();
        reader.onload = evt => {
            try {
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
                if (!raw.length) { toast.error('The uploaded file is empty'); return; }

                // Trim all keys and string values
                const cleaned = raw.map((row, i) => {
                    const clean = { _id: i };
                    Object.keys(row).forEach(k => {
                        const key = k.trim();
                        clean[key] = typeof row[k] === 'string' ? row[k].trim() : row[k];
                    });
                    return clean;
                });

                const validated = validateAllRows(cleaned, validationCtx);
                setRows(validated);
                setDuplicateActions({});
                // Default: every currently-valid, non-duplicate row is pre-selected for import.
                // Duplicates require an explicit Replace/Skip decision before they're included.
                setSelected(new Set(validated.filter(r => isRowValid(r) && !isDuplicateRow(r)).map(r => r._id)));
                setStep('preview');
            } catch {
                toast.error('Failed to parse file. Please use the provided template.');
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    // Update a single field, revalidate all rows (for duplicate detection), and
    // keep selection in sync: newly-invalid rows are dropped, the just-edited row
    // is auto-selected if it became valid, other rows keep their prior selection.
    const updateField = (idx, field, value) => {
        const updated = rows.map((row, i) =>
            i === idx ? { ...row, [field]: value } : row
        );
        const validated = validateAllRows(updated, validationCtx);
        setRows(validated);
        setSelected(prevSelected => {
            const next = new Set();
            validated.forEach((row, i) => {
                if (!isRowValid(row)) return;
                // Duplicates stay excluded unless the user has already chosen to replace
                if (isDuplicateRow(row) && duplicateActions[row._id] !== 'replace') return;
                if (prevSelected.has(row._id) || i === idx) next.add(row._id);
            });
            return next;
        });
    };

    const toggleRowSelected = id => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAllValid = () => {
        const selectableIds = rows
            .filter(r => isRowValid(r) && !(isDuplicateRow(r) && duplicateActions[r._id] !== 'replace'))
            .map(r => r._id);
        const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
        setSelected(allSelected ? new Set() : new Set(selectableIds));
    };

    // Select every valid, non-duplicate row — leaves duplicates out entirely
    const selectValidOnly = () => {
        setSelected(new Set(rows.filter(r => isRowValid(r) && !isDuplicateRow(r)).map(r => r._id)));
    };

    // Select every valid row, resolving all duplicates as "Replace" so nothing is left out
    const selectAllIncludingDuplicates = () => {
        const duplicateIds = rows.filter(isDuplicateRow).map(r => r._id);
        if (duplicateIds.length) {
            setDuplicateActions(prev => {
                const next = { ...prev };
                duplicateIds.forEach(id => { next[id] = 'replace'; });
                return next;
            });
        }
        setSelected(new Set(rows.filter(isRowValid).map(r => r._id)));
    };

    // Apply the same Skip/Replace decision to every duplicate row in one click
    const applyBulkDuplicateAction = action => {
        const duplicateIds = rows.filter(isDuplicateRow).map(r => r._id);
        if (!duplicateIds.length) return;
        setDuplicateActions(prev => {
            const next = { ...prev };
            duplicateIds.forEach(id => { next[id] = action; });
            return next;
        });
        setSelected(prev => {
            const next = new Set(prev);
            duplicateIds.forEach(id => {
                if (action === 'replace') next.add(id);
                else next.delete(id);
            });
            return next;
        });
    };

    // Set the TAT field on every currently-selected row in one click instead of editing row by row
    const applyBulkTat = () => {
        if (bulkTat === '' || isNaN(Number(bulkTat)) || Number(bulkTat) < 0 || selected.size === 0) return;
        const updated = rows.map(row => (selected.has(row._id) ? { ...row, 'TAT (Days)': bulkTat } : row));
        setRows(validateAllRows(updated, validationCtx));
    };

    const validCount = rows.filter(isRowValid).length;
    const invalidCount = rows.length - validCount;
    const duplicateCount = rows.filter(isDuplicateRow).length;
    const selectableCount = rows.filter(r => isRowValid(r) && !(isDuplicateRow(r) && duplicateActions[r._id] !== 'replace')).length;
    const selectedCount = selected.size;

    // Group identical-shaped errors together (e.g. "Duplicate of row 5" and "row 12"
    // collapse into one bucket) so 100 rows of errors reads as a handful of causes.
    const errorGroupMap = new Map();
    rows.forEach((row, idx) => {
        Object.entries(row._errors).forEach(([field, message]) => {
            const normalized = message.replace(/"[^"]*"/g, '"…"').replace(/row \d+/gi, 'row #');
            const key = `${field}|${normalized}`;
            if (!errorGroupMap.has(key)) {
                errorGroupMap.set(key, { field, message: normalized, rowNumbers: [] });
            }
            errorGroupMap.get(key).rowNumbers.push(idx + 1);
        });
    });
    const errorGroups = [...errorGroupMap.values()].sort((a, b) => b.rowNumbers.length - a.rowNumbers.length);

    const jumpToRow = rowNumber => {
        const el = document.getElementById(`bulk-upload-row-${rowNumber}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightRow(rowNumber);
        setTimeout(() => setHighlightRow(prev => (prev === rowNumber ? null : prev)), 1500);
    };

    const handleImport = async () => {
        const toImport = rows
            .filter(r => selected.has(r._id))
            .map(r =>
                isDuplicateRow(r) && duplicateActions[r._id] === 'replace'
                    ? { ...r, _replaceId: r._duplicateOf.id }
                    : r
            );
        if (!toImport.length) return;
        setImporting(true);
        try {
            await onImport(toImport);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div style={S.overlay} onClick={onClose}>
            <div style={S.modal} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={S.header}>
                    <h3 style={S.title}>
                        Bulk Upload — {modalTitle}
                    </h3>
                    <button style={S.closeBtn} onClick={onClose}>&times;</button>
                </div>

                {/* Body */}
                <div style={S.body}>
                    {step === 'upload' ? (
                        <div style={S.uploadCenter}>
                            <div style={{ fontSize: 52, marginBottom: 10 }}>📤</div>
                            <h4 style={{ margin: '0 0 6px', fontSize: 17 }}>
                                Bulk Upload {modalTitle}
                            </h4>
                            <p style={S.hint}>
                                Download the template, fill in your data, then upload the completed file.
                            </p>

                            <button style={S.templateBtn} onClick={downloadTemplate}>
                                ⬇ Download Template (.xlsx)
                            </button>

                            <div style={S.dropZone}>
                                <p style={{ color: '#374151', marginBottom: 8, fontSize: 14 }}>
                                    Click to upload your completed file
                                </p>
                                <p style={{ color: '#9ca3af', fontSize: 12, marginBottom: 14 }}>
                                    Accepted: .xlsx, .xls
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFile}
                                    style={{ display: 'none' }}
                                />
                                <button
                                    style={S.chooseBtn}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Choose File
                                </button>
                            </div>

                            <p style={S.tip}>
                                {isDoctors
                                    ? 'Hospital must match an existing hospital name; add City if the name is used in more than one city.'
                                    : isHospitals
                                    ? 'City must match an existing city name exactly.'
                                    : isTests
                                    ? 'If a test name already exists, you\'ll be asked to Replace (update TAT) or Skip it during preview.'
                                    : 'Test names must exactly match the master test list (case-sensitive, no extra spaces).'}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Summary bar */}
                            <div style={S.summaryBar}>
                                <span style={{ ...S.badge, background: '#dcfce7', color: '#166534' }}>
                                    ✓ Valid: {validCount}
                                </span>
                                <span style={{
                                    ...S.badge,
                                    background: invalidCount > 0 ? '#fee2e2' : '#dcfce7',
                                    color: invalidCount > 0 ? '#991b1b' : '#166534',
                                }}>
                                    ✗ Invalid: {invalidCount}
                                </span>
                                <span style={{ ...S.badge, background: '#dbeafe', color: '#1e40af' }}>
                                    Selected: {selectedCount}
                                </span>
                                {isTests && duplicateCount > 0 && (
                                    <span style={{ ...S.badge, background: '#fef3c7', color: '#92400e' }}>
                                        ⚠ Duplicates: {duplicateCount}
                                    </span>
                                )}
                                <span style={{ color: '#6b7280', fontSize: 13 }}>
                                    Total: {rows.length} rows
                                </span>
                                {invalidCount > 0 && (
                                    <span style={{ color: '#b45309', fontSize: 13 }}>
                                        Invalid rows are skipped — fix them to include, or import the valid rows as-is.
                                    </span>
                                )}
                            </div>

                            {/* Error breakdown — grouped so 100 rows of errors reads as a few causes */}
                            {errorGroups.length > 0 && (
                                <div style={S.errorSummaryBox}>
                                    <div style={S.errorSummaryTitle}>
                                        ⚠ {invalidCount} row{invalidCount !== 1 ? 's' : ''} need attention — grouped by issue:
                                    </div>
                                    {errorGroups.map(g => {
                                        const shown = g.rowNumbers.slice(0, 15);
                                        const extra = g.rowNumbers.length - shown.length;
                                        return (
                                            <div key={`${g.field}|${g.message}`} style={S.errorGroupRow}>
                                                <span style={S.errorGroupField}>{g.field}</span>
                                                <span style={S.errorGroupMsg}>{g.message}</span>
                                                <span style={S.errorGroupCount}>
                                                    {g.rowNumbers.length} row{g.rowNumbers.length !== 1 ? 's' : ''}
                                                </span>
                                                <span style={S.errorGroupRows}>
                                                    {shown.map(r => (
                                                        <button
                                                            key={r}
                                                            type="button"
                                                            style={S.rowChip}
                                                            onClick={() => jumpToRow(r)}
                                                            title={`Jump to row ${r}`}
                                                        >
                                                            #{r}
                                                        </button>
                                                    ))}
                                                    {extra > 0 && (
                                                        <span style={{ fontSize: 11, color: '#9ca3af' }}>+{extra} more</span>
                                                    )}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Bulk actions — set selection and duplicate/TAT decisions across many rows at once */}
                            <div style={S.bulkActionsBar}>
                                <span style={S.bulkActionsLabel}>Bulk actions:</span>
                                <button
                                    type="button"
                                    style={{ ...S.bulkBtn, opacity: invalidCount === 0 ? 0.5 : 1, cursor: invalidCount === 0 ? 'not-allowed' : 'pointer' }}
                                    onClick={exportInvalidRows}
                                    disabled={invalidCount === 0}
                                    title="Download invalid rows with their validation errors as an Excel file"
                                >
                                    Export Invalid Rows ({invalidCount})
                                </button>
                                <button type="button" style={S.bulkBtn} onClick={selectValidOnly}>
                                    Select Valid Only
                                </button>
                                <button type="button" style={S.bulkBtn} onClick={selectAllIncludingDuplicates}>
                                    Select All (Replace Duplicates)
                                </button>
                                <button type="button" style={S.bulkBtn} onClick={() => setSelected(new Set())}>
                                    Clear Selection
                                </button>
                                {isTests && duplicateCount > 0 && (
                                    <>
                                        <button type="button" style={{ ...S.bulkBtn, ...S.bulkBtnSkip }} onClick={() => applyBulkDuplicateAction('skip')}>
                                            Skip All Duplicates ({duplicateCount})
                                        </button>
                                        <button type="button" style={{ ...S.bulkBtn, ...S.bulkBtnReplace }} onClick={() => applyBulkDuplicateAction('replace')}>
                                            Replace All Duplicates ({duplicateCount})
                                        </button>
                                    </>
                                )}
                                {isTests && (
                                    <span style={S.bulkTatGroup}>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            placeholder="Set TAT"
                                            value={bulkTat}
                                            onChange={e => setBulkTat(e.target.value)}
                                            style={S.bulkTatInput}
                                        />
                                        <button
                                            type="button"
                                            style={{ ...S.bulkBtn, opacity: selectedCount === 0 ? 0.5 : 1 }}
                                            onClick={applyBulkTat}
                                            disabled={selectedCount === 0}
                                            title="Apply this TAT value to every selected row"
                                        >
                                            Apply TAT to Selected ({selectedCount})
                                        </button>
                                    </span>
                                )}
                            </div>

                            {/* Single datalist for test name autocomplete */}
                            <datalist id="bulk-upload-test-list">
                                {(isTests ? existingTests : testList || []).map(t => (
                                    <option key={t.id || t.TestName} value={t.TestName} />
                                ))}
                            </datalist>

                            {/* Datalist for doctor hospital autocomplete */}
                            {isDoctors && (
                                <datalist id="bulk-upload-hospital-list">
                                    {[...new Set((hospitals || []).map(h => h.hospitalName))].map(name => (
                                        <option key={name} value={name} />
                                    ))}
                                </datalist>
                            )}

                            {/* Datalist for city autocomplete (doctors + hospitals) */}
                            {(isDoctors || isHospitals) && (
                                <datalist id="bulk-upload-city-list">
                                    {[...new Set((cities || []).map(c => c.name))].map(name => (
                                        <option key={name} value={name} />
                                    ))}
                                </datalist>
                            )}

                            {/* Preview table */}
                            <div style={S.tableWrap}>
                                <table style={S.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...S.th, textAlign: 'center', width: 32 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectableCount > 0 && selectedCount === selectableCount}
                                                    ref={el => { if (el) el.indeterminate = selectedCount > 0 && selectedCount < selectableCount; }}
                                                    onChange={toggleSelectAllValid}
                                                    disabled={selectableCount === 0}
                                                    title="Select/deselect all eligible rows"
                                                />
                                            </th>
                                            <th style={S.th}>#</th>
                                            {headers.map(h => <th key={h} style={S.th}>{h}</th>)}
                                            <th style={S.th}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, idx) => {
                                            const hasError = Object.keys(row._errors).length > 0;
                                            const duplicate = isDuplicateRow(row);
                                            const duplicateAction = duplicateActions[row._id] || 'skip';
                                            const isUnresolvedDuplicate = duplicate && duplicateAction !== 'replace';
                                            const isSelected = selected.has(row._id);
                                            const isHighlighted = highlightRow === idx + 1;
                                            return (
                                                <tr
                                                    key={row._id ?? idx}
                                                    id={`bulk-upload-row-${idx + 1}`}
                                                    style={{
                                                        background: isHighlighted ? '#fef9c3' : hasError ? '#fef2f2' : duplicate ? (isUnresolvedDuplicate ? '#fffbeb' : '#f0fdf4') : isSelected ? '#f0fdf4' : '#fff',
                                                        transition: 'background 0.3s',
                                                        outline: isHighlighted ? '2px solid #eab308' : 'none',
                                                    }}
                                                >
                                                    <td style={{ ...S.td, textAlign: 'center', width: 32 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={hasError || isUnresolvedDuplicate}
                                                            onChange={() => toggleRowSelected(row._id)}
                                                            title={
                                                                hasError
                                                                    ? 'Fix errors to select this row'
                                                                    : isUnresolvedDuplicate
                                                                    ? 'Choose Replace to include this row'
                                                                    : 'Include this row in the import'
                                                            }
                                                        />
                                                    </td>
                                                    <td style={{ ...S.td, color: '#9ca3af', textAlign: 'center', width: 36 }}>
                                                        {idx + 1}
                                                    </td>
                                                    {headers.map(field => (
                                                        <EditCell
                                                            key={field}
                                                            field={field}
                                                            value={row[field] ?? ''}
                                                            error={row._errors[field]}
                                                            userTypes={userTypes}
                                                            isDoctors={isDoctors}
                                                            isHospitals={isHospitals}
                                                            isTests={isTests}
                                                            onChange={val => updateField(idx, field, val)}
                                                        />
                                                    ))}
                                                    <td style={{ ...S.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        {hasError ? (
                                                            <span style={S.invalidBadge}>Invalid</span>
                                                        ) : duplicate ? (
                                                            <div style={S.duplicateCell}>
                                                                <span style={S.duplicateTag}>
                                                                    <span style={S.duplicateDot} />
                                                                    Already exists
                                                                </span>
                                                                <div style={S.duplicateToggle} role="group" aria-label="Resolve duplicate test">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setDuplicateAction(row._id, 'skip')}
                                                                        style={{
                                                                            ...S.duplicateToggleBtn,
                                                                            ...S.duplicateToggleBtnLeft,
                                                                            ...(duplicateAction === 'skip' ? S.duplicateToggleBtnSkipActive : {}),
                                                                        }}
                                                                        title="Leave the existing test untouched"
                                                                    >
                                                                        Skip
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setDuplicateAction(row._id, 'replace')}
                                                                        style={{
                                                                            ...S.duplicateToggleBtn,
                                                                            ...(duplicateAction === 'replace' ? S.duplicateToggleBtnReplaceActive : {}),
                                                                        }}
                                                                        title="Update the existing test's TAT with this row's value"
                                                                    >
                                                                        Replace
                                                                    </button>
                                                                </div>
                                                                <span style={S.duplicateHint}>
                                                                    current TAT: {row._duplicateOf?.tat ?? '—'}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span style={S.validBadge}>Valid</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer */}
                            <div style={S.footer}>
                                <button
                                    style={S.backBtn}
                                    onClick={() => { setStep('upload'); setRows([]); setSelected(new Set()); }}
                                >
                                    ← Back to Upload
                                </button>
                                <button
                                    style={{
                                        ...S.importBtn,
                                        background: selectedCount > 0 ? '#27ae60' : '#9ca3af',
                                        cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                                    }}
                                    disabled={selectedCount === 0 || importing}
                                    onClick={handleImport}
                                >
                                    {importing ? 'Importing...' : `Import ${selectedCount} Selected Record${selectedCount !== 1 ? 's' : ''}`}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Editable cell ─────────────────────────────────────────────────────────────

const EditCell = ({ field, value, error, userTypes, isDoctors, isHospitals, isTests, onChange }) => {
    const inputBase = {
        width: '100%',
        padding: '5px 8px',
        border: `1px solid ${error ? '#f87171' : '#d1d5db'}`,
        borderRadius: 4,
        fontSize: 12,
        background: error ? '#fff1f1' : '#fff',
        boxSizing: 'border-box',
        outline: 'none',
    };

    let input;
    if (field === 'User Type') {
        input = (
            <select
                value={value}
                onChange={e => onChange(e.target.value)}
                style={{ ...inputBase, cursor: 'pointer' }}
            >
                <option value="">-- Select --</option>
                {userTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
        );
    } else if (field === 'Test' || field === 'Test Name') {
        input = (
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                style={inputBase}
                list="bulk-upload-test-list"
                placeholder="Type or select a test..."
            />
        );
    } else if (field === 'Hospital') {
        input = (
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                style={inputBase}
                list="bulk-upload-hospital-list"
                placeholder="Type or select a hospital..."
            />
        );
    } else if (field === 'City') {
        input = (
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                style={inputBase}
                list="bulk-upload-city-list"
                placeholder={isHospitals ? 'Type or select a city...' : 'Optional — disambiguates hospital'}
            />
        );
    } else if (isDoctors && (field === 'Doctor Name' || field === 'Mobile Number' || field === 'Email')) {
        input = (
            <input
                type={field === 'Email' ? 'email' : 'text'}
                value={value}
                onChange={e => onChange(e.target.value)}
                style={inputBase}
                placeholder={field === 'Mobile Number' ? '10-digit number' : field === 'Email' ? 'name@example.com' : 'Dr. Full Name'}
            />
        );
    } else if (isHospitals && (field === 'Hospital Name' || field === 'Address' || field === 'Pincode')) {
        input = (
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                style={inputBase}
                placeholder={field === 'Pincode' ? '6-digit pincode' : field === 'Address' ? 'Optional' : 'Hospital name'}
            />
        );
    } else if (isTests && field === 'TAT (Days)') {
        input = (
            <input
                type="number"
                value={value}
                onChange={e => onChange(e.target.value)}
                min="0"
                step="1"
                placeholder="e.g. 7"
                style={{ ...inputBase, minWidth: 80 }}
            />
        );
    } else {
        input = (
            <input
                type="number"
                value={value}
                onChange={e => onChange(e.target.value)}
                min="0"
                step="0.01"
                placeholder="—"
                style={{ ...inputBase, minWidth: 80 }}
            />
        );
    }

    const minWidth =
        field === 'Test' || field === 'Test Name' || field === 'Doctor Name' || field === 'Hospital Name' || field === 'Address' ? 200
        : field === 'User Type' || field === 'Hospital' || field === 'Email' ? 160
        : field === 'City' ? 130
        : 90;

    return (
        <td style={{ ...S.td, minWidth, verticalAlign: 'top' }}>
            {input}
            {error && <div style={S.cellError}>{error}</div>}
        </td>
    );
};

// ── Inline styles ─────────────────────────────────────────────────────────────

const S = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
    },
    modal: {
        background: '#fff', borderRadius: 10,
        width: '95vw', maxWidth: 1100, maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 24px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
    },
    title: { margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' },
    closeBtn: {
        background: 'none', border: 'none', fontSize: 26,
        cursor: 'pointer', color: '#6b7280', lineHeight: 1, padding: 0,
    },
    body: { padding: 24, overflowY: 'auto', flex: 1 },
    // upload step
    uploadCenter: { maxWidth: 500, margin: '0 auto', textAlign: 'center', padding: '8px 0' },
    hint: { color: '#6b7280', marginBottom: 20, fontSize: 14 },
    templateBtn: {
        padding: '10px 24px', background: '#1d6fa4', color: '#fff',
        border: 'none', borderRadius: 6, cursor: 'pointer',
        fontWeight: 600, fontSize: 14, marginBottom: 24, display: 'inline-block',
    },
    dropZone: {
        border: '2px dashed #d1d5db', borderRadius: 8,
        padding: '28px 24px', marginBottom: 14, background: '#f9fafb',
    },
    chooseBtn: {
        padding: '9px 22px', background: '#27ae60', color: '#fff',
        border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14,
    },
    tip: { fontSize: 12, color: '#9ca3af', marginTop: 8 },
    // preview step
    summaryBar: { display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' },
    badge: { padding: '4px 14px', borderRadius: 20, fontWeight: 600, fontSize: 13 },
    errorSummaryBox: {
        border: '1px solid #fecaca', background: '#fff7f7', borderRadius: 8,
        padding: '12px 14px', marginBottom: 14,
    },
    errorSummaryTitle: { fontWeight: 700, fontSize: 13, color: '#991b1b', marginBottom: 8 },
    errorGroupRow: {
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '5px 0', borderTop: '1px solid #fee2e2', fontSize: 12,
    },
    errorGroupField: {
        fontWeight: 700, color: '#7f1d1d', background: '#fee2e2',
        padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap',
    },
    errorGroupMsg: { color: '#374151', flex: '1 1 auto', minWidth: 140 },
    errorGroupCount: { color: '#991b1b', fontWeight: 600, whiteSpace: 'nowrap' },
    errorGroupRows: { display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' },
    rowChip: {
        border: '1px solid #fca5a5', background: '#fff', color: '#991b1b',
        borderRadius: 4, padding: '1px 6px', fontSize: 11, cursor: 'pointer', fontWeight: 600,
    },
    bulkActionsBar: {
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
        padding: '10px 12px', marginBottom: 12,
        background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
    },
    bulkActionsLabel: { fontSize: 12, fontWeight: 700, color: '#374151', marginRight: 2 },
    bulkBtn: {
        padding: '6px 12px', background: '#fff', color: '#374151',
        border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer',
        fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap',
    },
    bulkBtnSkip: { color: '#991b1b', border: '1px solid #fca5a5', background: '#fff7f7' },
    bulkBtnReplace: { color: '#166534', border: '1px solid #86efac', background: '#f5fdf8' },
    bulkTatGroup: { display: 'flex', gap: 6, alignItems: 'center' },
    bulkTatInput: {
        width: 80, padding: '6px 8px', fontSize: 12,
        border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box',
    },
    tableWrap: {
        overflowX: 'auto', overflowY: 'auto', maxHeight: '52vh',
        border: '1px solid #e5e7eb', borderRadius: 8,
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: {
        padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12,
        color: '#374151', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap',
        position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1,
    },
    td: { padding: '7px 10px', borderBottom: '1px solid #e5e7eb' },
    validBadge: {
        background: '#86efac', color: '#14532d',
        padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
    },
    invalidBadge: {
        background: '#fca5a5', color: '#7f1d1d',
        padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
    },
    duplicateCell: {
        display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center',
        padding: '2px 0',
    },
    duplicateTag: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: '#fef3c7', color: '#92400e',
        padding: '2px 9px', borderRadius: 999, fontSize: 10, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap',
    },
    duplicateDot: {
        width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', flexShrink: 0,
    },
    duplicateToggle: {
        display: 'inline-flex', border: '1px solid #f0c674', borderRadius: 7,
        overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    },
    duplicateToggleBtn: {
        border: 'none', background: '#fff', color: '#6b7280',
        fontSize: 11, fontWeight: 700, padding: '5px 12px', cursor: 'pointer',
        transition: 'background 0.15s, color 0.15s',
    },
    duplicateToggleBtnLeft: { borderRight: '1px solid #f0c674' },
    duplicateToggleBtnSkipActive: { background: '#fee2e2', color: '#991b1b' },
    duplicateToggleBtnReplaceActive: { background: '#dcfce7', color: '#166534' },
    duplicateHint: { fontSize: 10, color: '#92400e', opacity: 0.8 },
    cellError: { color: '#dc2626', fontSize: 10, marginTop: 3, lineHeight: 1.4 },
    footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 },
    backBtn: {
        padding: '9px 20px', background: '#f3f4f6', color: '#374151',
        border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer',
        fontWeight: 600, fontSize: 14,
    },
    importBtn: {
        padding: '9px 24px', color: '#fff', border: 'none',
        borderRadius: 6, fontWeight: 600, fontSize: 14,
    },
};

export default BulkUploadModal;
